import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { useScrollMemory } from '../../../../hooks/use-scroll-memory';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { useShipmentSelection } from '../../../../hooks/use-shipment-selection';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes, ScrollStatePaths } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { ActionEditContainerInfo } from '../components/ActionEditContainerInfo';
import ContainerRow from '../components/ContainerRow';
import { ShipmentBulkActions } from '../../../../transportation/components/shipments/ShipmentBulkActions';
import { ContainerRead, ContainerSummary } from '../../../../transportation/models/containers/_index';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';
import { ContainerSearchFilters } from '../../../../transportation/models/filters/container-search-filters';
import { getContainerSearchFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';
import '../../../../transportation/styles.scss';

export const ContainersScene = () => {

    const columns = ['containerId', 'status', 'name', 'driver', 'cutoff', 'departureTime'];

    const store                                 = useStore();
    const dispatch                              = useDispatch();
    const [service]                             = React.useState(new TransportationSvc());
    const [showInfoFlyout, setShowInfoFlyout]   = React.useState(false);
    const [editContainer, setEditContainer]     = React.useState(new ContainerRead());

    const [filterParams, setFilterParams]   = React.useState(() => {
        return getContainerSearchFilters(store.getState(), columns);
    });

    const [containers, setContainers]       = React.useState(() => {
        const initial = new PagedApiResult<ContainerSummary>();
        initial.pagination = filterParams.paginationInfo;
        initial.pagination.reusePaging = true;
        return initial;        
    });

    const [isBulkBusy, setIsBulkBusy] = React.useState(false);

    const { selected, selectedResetter, onSelectionFinish, resetSelection } = useShipmentSelection(
        containers.results,
        (row : any) => row.props.container
    );

    const scrollTo = useScrollMemory(
        ScrollStatePaths.SHIPMENTS_CONTAINERS,
        Routes.SHIPPING_CONTAINER_EDIT
    );

    useSearchParams(
        Routes.SHIPPING_CONTAINER_EDIT,
        transportActions.updateContainerSearchFilters(filterParams),
        transportActions.clearContainerSearch()
    );

    /**
     * OnSearch handler
     * @param filters The list of filters to apply to the search
     */
    const onSearch = async (filters: ContainerSearchFilters) => {
        resetSelection();
        const response = await service.search(ContainerSummary, filters);
        if (response.status === StatusCode.Success) {
            setContainers(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new ContainerSearchFilters(columns);
        setFilterParams(tmp);
    };

    const onFlyoutClose = async () => {
        setShowInfoFlyout(false);        
    };

    const onFlyoutSuccess = async (result : number) => {
        setEditContainer(new ContainerRead());
        setShowInfoFlyout(false);

        // For now we will navigate to the newly created pickup.
        // Could be revisited later with a call to search
        // await onSearch(filterParams);
        // onSearch(filterParams);
        navigateToPage(Routes.SHIPPING_CONTAINER_EDIT_WITH_ID.formattify(result));
    };

    return (<PageContent classes="containers-list" pageName={PageNames.TRANSPORTATION_CONTAINER_LIST}>
                <PageHeading>
                    <HasPermission requiredPermissions={[
                        Permissions.ShippingContainersEdit
                    ]}>
                        <button className="btn new attention" onClick={ () => setShowInfoFlyout(true) }>
                            Create Container
                        </button>
                    </HasPermission>
                </PageHeading>
                <PageBody>
                    <ShipmentBulkActions
                        type={ ShipmentType.Container }
                        shipments={ selected }
                        onRefresh={ () => onSearch(filterParams) }
                        onBusyChange={ setIsBulkBusy }>
                        <PagedList
                            noResultsMessage="No containers found, please search again."
                            searchCallback={ onSearch }
                            data={ containers }
                            selectableClass=""
                            filterProps={ filterParams }
                            onFilterUpdate={ onParamsUpdate }
                            onReset={ onParamsReset }
                            reduxDispatch={ dispatch }
                            selectedResetter={ selectedResetter }
                            enabledDrag={ !isBulkBusy }
                            onDragComplete={ onSelectionFinish }
                            rowRenderer={(container: ContainerSummary) => 
                                <ContainerRow 
                                    key={ container.id } 
                                    container={ container } 
                                />
                            }
                        />
                    </ShipmentBulkActions>

                    <ActionEditContainerInfo
                        visible={ showInfoFlyout }
                        onSuccess={ onFlyoutSuccess }
                        onClose={ onFlyoutClose }
                        container={ editContainer }
                    />

                </PageBody>
            </PageContent>);
    
};
